import { LogOut } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { useUser } from '@/contexts/UserContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { logger } from '@/services/logger';

/**
 * User menu component for sidebar footer
 * Displays current user and allows sign out and account switching
 */
export function UserMenu() {
  const { instance } = useMsal();
  const { account, switchAccount, availableAccounts } = useUser();

  /**
   * Handle user sign out
   */
  const handleSignOut = async () => {
    try {
      logger.trackEvent('UserSession.SignOut', {
        accountId: account.homeAccountId,
        username: account.username,
      });
      await instance.logoutRedirect({
        account: account,
      });
    } catch (error) {
      logger.error(error instanceof Error ? error : new Error('Sign out failed'), {
        context: 'UserMenu.handleSignOut',
      });
    }
  };

  /**
   * Handle account switching
   */
  const handleAccountSwitch = (accountId: string) => {
    if (accountId !== account.homeAccountId) {
      switchAccount(accountId);
    }
  };

  /**
   * Get user initials for avatar
   */
  const getInitials = (accountName?: string, username?: string) => {
    if (accountName) {
      const names = accountName.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    }
    if (username) {
      return username.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const hasMultipleAccounts = availableAccounts.length > 1;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <span className="text-xs font-medium">{getInitials(account.name, account.username)}</span>
              </div>
              <div className="flex flex-col gap-0.5 leading-none min-w-0">
                <span className="font-medium truncate">{account.name || account.username}</span>
                <span className="text-xs text-muted-foreground truncate">{account.username}</span>
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width]"
            align="start"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{account.name || account.username}</p>
                <p className="text-xs leading-none text-muted-foreground">{account.username}</p>
              </div>
            </DropdownMenuLabel>
            {hasMultipleAccounts && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Switch Account
                </DropdownMenuLabel>
                {availableAccounts
                  .filter(acc => acc.homeAccountId !== account.homeAccountId)
                  .map((acc) => (
                    <DropdownMenuItem
                      key={acc.homeAccountId}
                      onSelect={() => handleAccountSwitch(acc.homeAccountId)}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <div className="bg-muted flex aspect-square size-6 items-center justify-center rounded">
                          <span className="text-xs font-medium">{getInitials(acc.name, acc.username)}</span>
                        </div>
                        <div className="flex flex-col gap-0.5 leading-none min-w-0 flex-1">
                          <span className="text-sm truncate">{acc.name || acc.username}</span>
                          <span className="text-xs text-muted-foreground truncate">{acc.username}</span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleSignOut}>
              <LogOut className="mr-2 size-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
